import { z } from "zod";
import { resolveSampleContent } from "./sample-copy";

const text = z.string().trim().max(16000);
export const sampleContentSchema = z.object({
  translations: z.object({
    nl: z.object({
      name: text,
      intro: text,
      description: text,
      applications: text,
      care: text,
    }),
    en: z
      .object({
        name: text,
        intro: text,
        description: text,
        applications: text,
        care: text,
      })
      .optional(),
    de: z
      .object({
        name: text,
        intro: text,
        description: text,
        applications: text,
        care: text,
      })
      .optional(),
    fr: z
      .object({
        name: text,
        intro: text,
        description: text,
        applications: text,
        care: text,
      })
      .optional(),
  }),
  specs: z
    .array(
      z.object({
        label: z.string().trim().min(1).max(120),
        value: z.string().trim().min(1).max(1000),
      }),
    )
    .max(60)
    .default([]),
  relatedIds: z.array(z.string().max(80)).max(30).default([]),
  notes: text.default(""),
  sources: z
    .array(
      z.object({
        supplier: z.string().max(160),
        kind: z.enum([
          "supplier-api",
          "supplier-document",
          "catalog-reference",
        ]),
        url: z.string().max(1500),
        reference: z.string().max(1000),
        checkedAt: z.string().max(60),
        scope: z.string().max(1000),
      }),
    )
    .max(200)
    .optional(),
});
export const sampleInputSchema = z.object({
  kind: z.enum(["material", "finish"]),
  name: z.string().trim().min(2).max(180),
  slug: z
    .string()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    .max(120),
  sampleCode: z.string().trim().min(2).max(60),
  status: z.enum(["draft", "published", "archived"]),
  content: sampleContentSchema,
});
export type SampleContent = z.infer<typeof sampleContentSchema>;
export const emptySampleContent: SampleContent = {
  translations: {
    nl: { name: "", intro: "", description: "", applications: "", care: "" },
  },
  specs: [],
  relatedIds: [],
  notes: "",
};

export function sampleKind(
  key: string,
  label = "",
): "material" | "finish" | null {
  const s = `${key} ${label}`.toLowerCase();
  // These are controls about a material/finish, not samples themselves.
  if (
    /color|colour|kleur|length|lengte|thickness|dikte|grain|looprichting|manual|handleiding|application|all_sides_equal|finishing_mode|finish_side/.test(
      s,
    )
  )
    return null;
  if (/material|materiaal|paper|papier|substrate|karton|vinyl/.test(s))
    return "material";
  if (
    /finish|afwerk|lamina|laminat|coating|vernis|varnish|emboss|foil|foliedruk|spot.?uv|veredel|type_glue|verlijming/.test(
      s,
    )
  )
    return "finish";
  return null;
}
export function catalogOptions(options: unknown) {
  if (!Array.isArray(options)) return [];
  return options.flatMap((o: unknown) => {
    if (!o || typeof o !== "object") return [];
    const option = o as Record<string, unknown>;
    const key = String(option.id || option.name || "");
    const kind = sampleKind(key, String(option.label || ""));
    if (!kind || !Array.isArray(option.values)) return [];
    return option.values.flatMap((v: unknown) => {
      if (!v || typeof v !== "object") return [];
      const value = v as Record<string, unknown>;
      if (typeof value.value !== "string" && typeof value.value !== "number")
        return [];
      return [
        {
          kind,
          optionKey: key,
          optionValue: String(value.value),
          sourceLabel: String(value.label || value.value).slice(0, 500),
        },
      ];
    });
  });
}
export function publicationIssues(
  input: z.infer<typeof sampleInputSchema>,
  assets: { approved: boolean; role: string }[],
) {
  const issues: string[] = [];
  if (
    !input.content.translations.nl.intro.trim() ||
    !input.content.translations.nl.description.trim()
  )
    issues.push("Vul de introductie en beschrijving in.");
  if (!assets.some((a) => a.approved && a.role === "detail"))
    issues.push("Keur eerst een echte detailfoto goed.");
  return issues;
}
/**
 * Content as shown to customers. With the entry's name and kind, template
 * filler about the photos is replaced by real material copy in all four
 * languages (see sample-copy.ts).
 */
export function publicSampleContent(
  value: unknown,
  entry?: { name: string; kind: string },
): SampleContent {
  const parsed = sampleContentSchema.safeParse(value);
  const content = parsed.success
    ? { ...parsed.data, notes: "", sources: undefined }
    : emptySampleContent;
  return entry ? resolveSampleContent(content, entry) : content;
}

export function sampleProductLink(
  links: { id: string; variantId: string | null }[],
  variantId: string,
) {
  return (
    (variantId
      ? links.find((l) => l.variantId === variantId) ||
        links.find((l) => l.variantId === null)
      : links[0]
    )?.id || null
  );
}

export function sortSampleVariants<T extends { name: string }>(variants: T[]): T[] {
  return [...variants].sort((left, right) =>
    left.name.localeCompare(right.name, "nl", { numeric: true, sensitivity: "base" }),
  );
}

export function photographedSampleVariants<T extends { id: string }>(
  variants: T[],
  assets: { variantId: string | null; role: string }[],
): T[] {
  const photographedIds = new Set(
    assets.filter((asset) => asset.role === "detail" && asset.variantId).map((asset) => asset.variantId),
  );
  return variants.filter((variant) => photographedIds.has(variant.id));
}

export function sampleOptionAsset<
  T extends { variantId: string | null; role: string },
>(assets: T[], variantId: string | null) {
  // Exact physical variant beats even a newer generic generated image.
  const choose = (id: string | null) =>
    assets.find((a) => a.variantId === id && a.role === "option") ||
    assets.find((a) => a.variantId === id && a.role === "detail");
  // Daarna een foto van hetzelfde materiaal in een ander gewicht: 135 g en
  // 170 g gesatineerd MC zien er op een tegel hetzelfde uit, en zonder deze
  // stap kreeg ieder niet apart gefotografeerd gewicht geen afbeelding.
  return (variantId ? choose(variantId) : undefined) || choose(null)
    || assets.find((a) => a.role === "option") || assets.find((a) => a.role === "detail");
}
